/**
 * useTableSettings — хук для сохранения пользовательских настроек таблицы.
 *
 * Сохраняет ширины колонок (после ручного ресайза) в localStorage.
 * Все таблицы хранятся в одном ключе `plng-table-settings`.
 * Паттерн аналогичен useCanvasLayout — version counter для принудительного перечитывания.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HandsontableWrapperRef } from '../HandsontableWrapperSimple';

const STORAGE_KEY = 'plng-table-settings';

/** Настройки одной таблицы */
interface TableEntry {
  colWidths?: Record<string, number>;
}

/** Всё хранилище: { [tableId]: TableEntry } */
type SettingsStore = Record<string, TableEntry>;

// ── Helpers для работы с единым хранилищем ───────────────────────────

function readStore(): SettingsStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SettingsStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: SettingsStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Игнорируем ошибки localStorage (квота, private mode)
  }
}

// ── Public API ────────────────────────────────────────────────────────

export interface UseTableSettingsResult {
  /** Сохранённые ширины колонок (null если нет) */
  savedColWidths: Record<string, number> | null;
  /**
   * Callback для Handsontable afterColumnResize.
   * Сигнатура: (newSize, column, isDoubleClick) — соответствует HT API.
   */
  handleColumnResize: (newSize: number, column: number, isDoubleClick: boolean) => void;
  /** Применить сохранённые ширины через API плагина manualColumnResize */
  applyToInstance: (hotRef: React.RefObject<HandsontableWrapperRef | null>) => void;
  /** Сбросить сохранённые настройки и обновить таблицу до дефолтных ширин */
  resetSettings: (hotRef?: React.RefObject<HandsontableWrapperRef | null>) => void;
  /** Есть ли сохранённые пользовательские настройки */
  hasCustomSettings: boolean;
}

/**
 * Хук для персистенции пользовательских настроек колонок таблицы.
 *
 * @param tableId — уникальный идентификатор таблицы (например, 'stage1-planning')
 * @param enabled — включена ли персистенция (default: true)
 */
export function useTableSettings(tableId: string, enabled = true): UseTableSettingsResult {
  const [version, setVersion] = useState(0);

  // Ref для debounce записи в localStorage
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref для текущих colWidths — обновляется синхронно, пишется в LS с debounce
  const pendingWidthsRef = useRef<Record<string, number> | null>(null);
  // Guard: пока true — игнорируем afterColumnResize (HT может его эмитить
  // во время clearManualSize/render внутри resetSettings, что иначе вернёт
  // hasCustomSettings=true сразу после сброса).
  const isResettingRef = useRef(false);

  const savedColWidths = useMemo<Record<string, number> | null>(() => {
    if (!enabled) return null;
    const store = readStore();
    const entry = store[tableId];
    if (!entry?.colWidths || Object.keys(entry.colWidths).length === 0) return null;
    return entry.colWidths;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, enabled, version]);

  // Инициализируем pending ref из прочитанных данных
  if (pendingWidthsRef.current === null && savedColWidths) {
    pendingWidthsRef.current = { ...savedColWidths };
  }

  // Отдельный state-флаг: чтобы кнопка «Сбросить настройки колонок» появлялась
  // сразу после первого ресайза, а не только после перезагрузки страницы.
  // savedColWidths мемоизирован на version и не переобновляется от debounce-записи.
  const [hasCustomSettings, setHasCustomSettings] = useState<boolean>(() => savedColWidths !== null);

  // Синхронизируем state с savedColWidths, когда тот меняется
  // (например, при resetSettings → version++ → savedColWidths пересчитывается).
  useEffect(() => {
    setHasCustomSettings(savedColWidths !== null);
  }, [savedColWidths]);

  /**
   * afterColumnResize callback.
   * Сигнатура HT: (newSize: number, column: number, isDoubleClick: boolean)
   */
  const handleColumnResize = useCallback(
    (newSize: number, column: number) => {
      if (!enabled) return;
      if (isResettingRef.current) return;

      const current = pendingWidthsRef.current ?? {};
      current[String(column)] = Math.round(newSize);
      pendingWidthsRef.current = current;

      // Мгновенно показываем кнопку «Сбросить настройки колонок»,
      // не дожидаясь debounce-записи в localStorage.
      setHasCustomSettings(true);

      // Debounce запись в localStorage (300ms)
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
      writeTimerRef.current = setTimeout(() => {
        const store = readStore();
        store[tableId] = { ...store[tableId], colWidths: { ...current } };
        writeStore(store);
        writeTimerRef.current = null;
      }, 300);
    },
    [enabled, tableId],
  );

  /** Применить сохранённые ширины к уже инициализированному HT инстансу */
  const applyToInstance = useCallback(
    (hotRef: React.RefObject<HandsontableWrapperRef | null>) => {
      const widths = pendingWidthsRef.current ?? savedColWidths;
      if (!widths || !enabled) return;
      const hot = hotRef.current?.getHotInstance?.();
      if (!hot) return;

      const plugin = hot.getPlugin('manualColumnResize');
      const MIN_WIDTH = 30; // защита от нулевых/слишком узких колонок из persisted state
      for (const [col, width] of Object.entries(widths)) {
        plugin.setManualSize(Number(col), Math.max(MIN_WIDTH, width));
      }
      hot.render();
    },
    [savedColWidths, enabled],
  );

  const resetSettings = useCallback(
    (hotRef?: React.RefObject<HandsontableWrapperRef | null>) => {
      isResettingRef.current = true;
      try {
        // Удаляем из единого хранилища
        const store = readStore();
        delete store[tableId];
        // Чистим пустое хранилище
        if (Object.keys(store).length === 0) {
          try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        } else {
          writeStore(store);
        }
        pendingWidthsRef.current = null;
        if (writeTimerRef.current) {
          clearTimeout(writeTimerRef.current);
          writeTimerRef.current = null;
        }

        // Сбрасываем ширины на инстансе — clearManualSize для всех колонок
        if (hotRef) {
          const hot = hotRef.current?.getHotInstance?.();
          if (hot) {
            const plugin = hot.getPlugin('manualColumnResize');
            const colCount = hot.countCols();
            for (let i = 0; i < colCount; i++) {
              plugin.clearManualSize(i);
            }
            hot.render();
          }
        }

        // Синхронно скрываем кнопку — не дожидаясь useEffect через savedColWidths.
        setHasCustomSettings(false);
        setVersion((v) => v + 1);
      } finally {
        // Снимаем guard на следующем тике, чтобы поглотить любые отложенные
        // afterColumnResize, которые HT мог поставить в очередь во время render().
        setTimeout(() => { isResettingRef.current = false; }, 0);
      }
    },
    [tableId],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    };
  }, []);

  return {
    savedColWidths,
    handleColumnResize,
    applyToInstance,
    resetSettings,
    hasCustomSettings,
  };
}
